/**
 * Shared status for background AI-mapping runs.
 *
 * - Resumes polling on mount if the server still reports `processing`
 * - Polls every 2s while active
 * - Stops on completed / failed (or after a long safety timeout)
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { runStatusApi, type MappingEntityType, type RunStatus } from '../api/runStatusApi';

const POLL_MS = 2000;
const MAX_POLL_MS = 30 * 60 * 1000;

export interface UseMappingRunProgressOptions {
    /** Called when a poll succeeds (typically to refresh suggestions). */
    onTick?: () => void;
    /** Called once when the run ends in failure. */
    onFailed?: (message: string) => void;
}

export function useMappingRunProgress(
    locationId: string | null | undefined,
    entityType: MappingEntityType,
    options: UseMappingRunProgressOptions = {},
) {
    const [isPolling, setIsPolling] = useState(false);
    const [runProgress, setRunProgress] = useState<RunStatus | null>(null);

    // Keep callbacks in refs so the poll loop is not torn down every parent render.
    const onTickRef = useRef(options.onTick);
    const onFailedRef = useRef(options.onFailed);
    onTickRef.current = options.onTick;
    onFailedRef.current = options.onFailed;

    const startPolling = useCallback(() => {
        setRunProgress(null);
        setIsPolling(true);
    }, []);

    // Resume watch if a run is already in flight (page refresh / re-navigation).
    useEffect(() => {
        if (!locationId) {
            setIsPolling(false);
            setRunProgress(null);
            return;
        }
        let cancelled = false;
        runStatusApi
            .get(locationId, entityType)
            .then(run => {
                if (cancelled) return;
                setRunProgress(run);
                if (run.status === 'processing') {
                    setIsPolling(true);
                }
            })
            .catch(() => {
                /* ignore — trigger + poll still works without the resume probe */
            });
        return () => {
            cancelled = true;
        };
    }, [locationId, entityType]);

    // Active poll loop while isPolling.
    useEffect(() => {
        if (!isPolling || !locationId) return;

        let cancelled = false;

        const pollOnce = async () => {
            try {
                onTickRef.current?.();
                const run = await runStatusApi.get(locationId, entityType);
                if (cancelled) return;
                setRunProgress(run);
                if (run.status === 'failed') {
                    onFailedRef.current?.(
                        run.detail
                            ? `Mapping failed: ${run.detail}`
                            : 'Mapping failed. Please try again.',
                    );
                    setIsPolling(false);
                } else if (run.status === 'completed') {
                    setIsPolling(false);
                }
            } catch {
                /* keep spinner until timeout — transient network blips */
            }
        };

        void pollOnce();
        const interval = setInterval(() => { void pollOnce(); }, POLL_MS);
        const maxTimeout = setTimeout(() => {
            if (!cancelled) setIsPolling(false);
        }, MAX_POLL_MS);

        return () => {
            cancelled = true;
            clearInterval(interval);
            clearTimeout(maxTimeout);
        };
    }, [isPolling, locationId, entityType]);

    return {
        isPolling,
        runProgress,
        startPolling,
    };
}
